import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn, TTheme } from '@feathr/components';
import { ContextMenu, Pill, TableColumnHeader, Time } from '@feathr/components';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISampleImport } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';
import buildPillColumn from './buildPillColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const STATUS_THEME: Record<ISampleImport['status'], TTheme> = {
  completed: 'green',
  'in-progress': 'cyan',
  failed: 'red',
};

const STATUS_LABEL: Record<ISampleImport['status'], string> = {
  completed: 'Completed',
  'in-progress': 'In progress',
  failed: 'Failed',
};

const SOURCE_LABEL: Record<ISampleImport['source'], string> = {
  csv: 'CSV',
  salesforce: 'Salesforce NPSP',
  'raisers-edge': "Raiser's Edge NXT",
  stripe: 'Stripe',
};

interface IImportsColumnsArgs {
  base: string;
  t: TFunction;
}

function importsColumns({ base, t }: IImportsColumnsArgs): Array<IColumn<ISampleImport>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
    },
    {
      accessor: 'source',
      id: 'source',
      Header: TableColumnHeader({ title: t('Source'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 200,
      Cell({ original }): JSX.Element {
        return <Pill theme={'neutral'}>{SOURCE_LABEL[original.source]}</Pill>;
      },
    },
    {
      accessor: 'recordCount',
      id: 'recordCount',
      Header: TableColumnHeader({ title: t('Records'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.recordCount).format('0,0')}</>;
      },
    },
    buildPillColumn<ISampleImport, ISampleImport['status']>({
      accessor: 'status',
      labelMap: STATUS_LABEL,
      t,
      themeMap: STATUS_THEME,
      title: t('Status'),
    }),
    {
      accessor: 'startedAt',
      id: 'startedAt',
      Header: TableColumnHeader({ title: t('Started'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 180,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.timeFromNow} timestamp={original.startedAt} />;
      },
    },
    buildOptionsColumn<ISampleImport>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('view')}>
            {t('View details')}
          </ContextMenu.Item>
          <DemoMenuItem action={'recalculate'} label={t('Re-run')} />
          <DemoMenuItem action={'delete'} isDestructive={true} label={t('Delete')} />
        </>
      ),
    }),
  ];
}

export default importsColumns;
